import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HeaderProps } from './Header.types';
import HeaderView from './Header.view';
import { logOut } from '../../../store/reducers/authorization.reducer';
import { logout as logoutAuth } from '../../../domains/auth/infrastructure/slices/authSlice';
import { userProfileApi } from '../../../domains/user/infrastructure/api/user.api';
import { paths } from '../../routes/paths';
import { RouteEnum } from '../../routes/routeEnum';
import { useDefaultHomePath, useRolePath } from '../../routes/useRolePath';

/**
 * Header Component
 * Top application header following DDD principles
 *
 * DDD Layer: UI (Presentation)
 * Responsibilities:
 * - User interaction handling
 * - State management for UI
 * - Navigation logic
 * - Integration with Redux store
 */
const Header: React.FC<HeaderProps> = ({
  className,
  showProfile = true,
  showNotifications = true,
  showLanguageSelector = false,
  logo,
  onLogoClick,
  navigationItems,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toRolePagePath = useRolePath();
  const homePath = useDefaultHomePath();

  // Local UI state
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isNotificationsOpen, setNotificationsOpen] = useState(false);

  // Get data from Redux store
  const user = useSelector((state: any) => state.auth?.user);
  const company = useSelector((state: any) => state.dynamic?.company);

  // Handlers
  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
    } else {
      navigate(homePath);
    }
  };

  const handleProfileMenuToggle = () => {
    setMenuOpen(!isMenuOpen);
  };

  const handleNotificationsToggle = () => {
    setNotificationsOpen(!isNotificationsOpen);
  };

  const handleLogout = () => {
    dispatch(userProfileApi.util.resetApiState());
    dispatch(logoutAuth());
    dispatch(logOut());
    navigate(RouteEnum.SIGN_IN);
  };

  const profilePath = toRolePagePath(paths.settingsProfile);
  const settingsPath = toRolePagePath(paths.settings);

  // Prepare user profile data
  const userProfile = user
    ? {
        id: user.id,
        fullName: user.fullName || `${user.firstName} ${user.lastName}`,
        email: user.email,
        avatar: user.avatar,
        role: user.role || 'student',
      }
    : null;

  // Prepare company data
  const companyData = company
    ? {
        name: company.name,
        logo: company.logo,
      }
    : null;

  // Pass props to view
  const viewProps = {
    className,
    showProfile,
    showNotifications,
    showLanguageSelector,
    logo: logo || companyData?.logo,
    onLogoClick: handleLogoClick,
    navigationItems,
    userProfile,
    companyData,
    isMenuOpen,
    isNotificationsOpen,
    onProfileMenuToggle: handleProfileMenuToggle,
    onNotificationsToggle: handleNotificationsToggle,
    onLogout: handleLogout,
    profileTo: profilePath,
    settingsTo: settingsPath,
    t,
  };

  return <HeaderView {...viewProps} />;
};

export default Header;
